"use client";

import { Heart, Leaf, Sprout } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hygiene() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imgRef,
    isVisible: imgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const principles = [{
    icon: Sprout,
    title: "Simple hygiene habits for busy families",
    body: "Practical, easy-to-follow routines that make staying clean effortless for parents and children alike."
  }, {
    icon: Leaf,
    title: "Gentle, portable formulas for daily protection",
    body: "Thoughtfully crafted mists that prioritize safety and convenience, wherever your day takes you."
  }, {
    icon: Heart,
    title: "A community built on peace of mind",
    body: "Join a network of parents and caregivers sharing tips on keeping your loved ones safe and prepared."
  }];
  return <section data-section-id="7523" id="hygiene-for-every-lifestyle" className="bg-background text-foreground py-24 md:py-32 px-4 sm:px-6 lg:px-10 overflow-hidden">
      <div className="max-w-[1240px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div ref={imgRef} className={`lg:col-span-5 order-1 transition-all duration-700 ease-out ${imgVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div className="relative">
              {}
              <div className="relative rounded-lg overflow-hidden border border-border aspect-[4/5]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/hygiene_mist_application.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/hygiene_mist_application.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/hygiene_mist_application.png-wvc-sizes" alt="Parent applying B.Y.E. Hygiene Mist to hands" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>

              {}
              <div aria-hidden="true" className="hidden sm:block absolute -bottom-10 -right-6 w-64 h-64 rounded-lg blur-[40px] opacity-70 pointer-events-none" style={{
              background: "var(--accent)",
              animation: "bloomDrift 16s ease-in-out infinite"
            }} />

              {}
              <div className="hidden sm:block relative mt-8 ml-12 w-[55%] aspect-square rounded-lg overflow-hidden border border-border shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/hygiene_travel_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/hygiene_travel_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1711121/img/hygiene_travel_kit.png-wvc-sizes" alt="Portable hygiene mist bottle in a travel bag" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>

          {}
          <div ref={headRef} className={`lg:col-span-7 order-2 transition-all duration-700 ease-out ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-5">
              Our approach
            </p>

            <h2 className="font-serif font-default text-4xl sm:text-5xl md:text-[3.4rem] leading-[1.08] tracking-tight text-foreground mb-6">
              Hygiene,{" "}
              <span className="italic font-normal">simplified</span>
            </h2>

            <p className="text-base sm:text-lg md:text-xl leading-relaxed text-muted-foreground max-w-[52ch] mb-10">
              At B.Y.E. Hygiene Mist™, we believe in being prepared, not afraid. We provide portable, 
              gentle solutions that help you maintain hygiene standards wherever you go, ensuring 
              peace of mind for you and your family.
            </p>

            <ul className="mb-10">
              {principles.map((item, i) => {
              const Icon = item.icon;
              return <li key={i} data-index={i} className={`transition-all duration-700 ease-out ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                transitionDelay: `${(i + 1) * 70}ms`
              }}>
                    {i > 0 && <Separator className="bg-border" />}
                    <div className="flex items-start gap-5 py-5">
                      <Icon className="size-7 text-foreground shrink-0 mt-0.5" strokeWidth={1.5} />
                      <div className="flex-1">
                        <h4 className="font-default text-base sm:text-lg font-semibold text-foreground leading-snug mb-1.5">
                          {item.title}
                        </h4>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                          {item.body}
                        </p>
                      </div>
                    </div>
                  </li>;
            })}
            </ul>

            <Link to="/OurStoryPage" className="inline-block text-foreground font-medium underline underline-offset-4 decoration-border hover:decoration-primary transition-colors duration-200">
              Read our story
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes bloomDrift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(24px, -20px); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="bloomDrift"] { animation: none !important; }
        }
      `}</style>
    </section>;
}